/**
 * Şablonlar — the reference's `data-screen="templates"`.
 *
 * Ported against the rendered DOM of `Screens.templates` / `renderTemplateGrid` (`m08-admin.js`):
 * the static page head, the filter toolbar the reference builds once into `#tplFilters`, and one
 * `.tpl-card` per template followed by the `.tpl-new` card. Every card draws its own thumbnail
 * through the shared render engine, from the template's CURRENT RELEASE when it has one and from
 * its draft otherwise — never from real recipient data (rule 12: a preview shows sample values).
 *
 * The card actions are the reference's four — Düzenle links into the editor, Önizle opens the
 * short/long preview dialog, Kopyala makes a draft copy and Arşivle asks before it archives —
 * plus one the approved revision added: «Adını değiştir», because renaming a template is a library
 * act and the editor's own name box used to write only to this browser.
 *
 * «Kopyala» and «Adını değiştir» share one dialog and one name box. Neither writes anything until
 * its own button is pressed, and what they change is the MANAGEMENT name: not the printed title,
 * not the short code, not a public code, and not any published release.
 */
import { type ReactElement, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAdoptTemplateCounts } from '../data/adopt';
import { useV07Commands } from '../data/commands-context';
import { useV07Queries } from '../data/queries-context';
import { useV07Role } from '../data/session';
import { useDemoData, useSettings } from '../data/store';
import type { DemoTemplate } from '../data/types';
import { useRead } from '../data/use-read';
import { currentRelease, fmtDate, fmtNum, TEMPLATE_STATUS_TR, trLower } from '../lib/reference';
import { suggestedCopyName } from '../lib/copy-name';
import { type SampleMode, templateThumbSVG } from '../lib/render';
import {
  CREDENTIAL_TYPES,
  draftTitle,
  duplicateTemplateRecord,
  layoutOf,
  SUBJECT_TYPES,
  templateTypeLabel,
  tplCount,
} from '../lib/template-list';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';

interface TplFilters {
  q: string;
  type: string;
  holder: string;
  status: string;
  sort: string;
}

/**
 * The reference keeps its filter state in a module-level `TPL`, so leaving the screen and coming
 * back keeps the search and the sort. Mirroring it here keeps that behaviour; the component state
 * is what re-renders.
 */
const TPL: TplFilters = { q: '', type: '', holder: '', status: '', sort: 'updated' };

function statusPillClass(status: string): string {
  if (status === 'published') return 'pill-active';
  if (status === 'draft') return 'pill-info';
  return 'pill-neutral';
}

export function TemplatesScreen(): ReactElement {
  const db = useDemoData();
  const queries = useV07Queries();
  /*
   * The per-template credential counts, from the server.
   *
   * This screen prints «N kayıt» beside every template and can sort by it, and the editor's publish
   * gate asks the same question before it says a release may still change. Counting the store would
   * answer with whatever page of the register happened to be loaded.
   */
  const countsRead = useRead('templates:counts', () => queries.registerSummary());
  useAdoptTemplateCounts(countsRead.value?.byTemplate ?? null);
  const settings = useSettings();
  const commands = useV07Commands();
  const role = useV07Role();
  const navigate = useNavigate();
  /*
   * `template.clone` and `template.archive` are both A·A·D·D, and so is `template.create` behind
   * the «+ Yeni Şablon» card. An operator reads the library and cannot change it; showing them a
   * control the backend will refuse is the decorative button §11 forbids. `role === null` is the
   * visual acceptance demo, which has no session and must see the reference's own screen.
   */
  const mayEdit = role === null || role === 'owner' || role === 'admin';
  const toast = useToast();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<TplFilters>(() => ({ ...TPL }));
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<SampleMode>('short');
  const [archiveId, setArchiveId] = useState<string | null>(null);
  /*
   * The copy and the rename are the same shape: a template, a name box, and a request that has not
   * been made yet. Both keep what was typed when the server refuses, because a refusal that empties
   * the box teaches people to retype blind.
   */
  const [copyFor, setCopyFor] = useState<DemoTemplate | null>(null);
  const [renameFor, setRenameFor] = useState<DemoTemplate | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /* Six full-size SVGs; re-drawing them on every keystroke in the search box is avoidable work. */
  const thumbs = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of db.templates) map.set(t.id, templateThumbSVG(t, { publicBase: settings.publicBase }));
    return map;
  }, [db.templates, settings.publicBase]);

  const apply = (patch: Partial<TplFilters>): void => {
    Object.assign(TPL, patch);
    setFilters({ ...TPL });
  };

  const q = trLower(filters.q.trim());
  const list = db.templates.filter((t) => {
    const lay = layoutOf(t);
    return (
      (!filters.type || lay.credentialType === filters.type) &&
      (!filters.holder || lay.subjectType === filters.holder) &&
      (!filters.status || t.status === filters.status) &&
      (!q || trLower(`${t.name} ${t.code} ${draftTitle(t, 'tr')} ${templateTypeLabel(t)}`).includes(q))
    );
  });
  if (filters.sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  else if (filters.sort === 'count') list.sort((a, b) => tplCount(db, b.id) - tplCount(db, a.id));
  else list.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));

  const openNew = (): void => {
    void navigate('/admin/templates/new');
  };

  const openCopy = (t: DemoTemplate): void => {
    setCopyFor(t);
    setNameDraft(
      suggestedCopyName(
        t.name,
        db.templates.map((x) => x.name),
      ),
    );
    setFormError(null);
    setBusy(false);
  };

  const openRename = (t: DemoTemplate): void => {
    setRenameFor(t);
    setNameDraft(t.name);
    setFormError(null);
    setBusy(false);
  };

  const closeNameDialog = (): void => {
    if (busy) return;
    setCopyFor(null);
    setRenameFor(null);
    setFormError(null);
  };

  /** The copy is created only here, with the name that is in the box when it is pressed. */
  const createCopy = async (t: DemoTemplate): Promise<void> => {
    const name = nameDraft.trim();
    if (name === '') {
      setFormError('Yeni şablon adı boş olamaz.');
      return;
    }
    setBusy(true);
    try {
      const copy = { ...duplicateTemplateRecord(t, new Set(db.templates.map((x) => x.code))), name };
      const r = await commands.duplicateTemplate({ templateId: t.id, copy });
      if (!r.ok) {
        /* The typed name survives a refusal; the dialog stays open on it. */
        setFormError(r.message ?? 'Kopya oluşturulamadı');
        return;
      }
      setCopyFor(null);
      toast('Şablonun kopyası taslak olarak oluşturuldu');
      /* The id the command reports: the server's for the product, the local copy's for the demo. */
      void navigate(`/admin/templates/${r.message ?? copy.id}/editor`);
    } finally {
      setBusy(false);
    }
  };

  const saveName = async (t: DemoTemplate): Promise<void> => {
    const name = nameDraft.trim();
    if (name === '') {
      setFormError('Şablon adı boş olamaz.');
      return;
    }
    if (name === t.name) {
      setRenameFor(null);
      return;
    }
    setBusy(true);
    try {
      /* `expectedName` is the name this card is showing; the server refuses the write if the
         template was renamed somewhere else in the meantime, rather than overwriting it. */
      const r = await commands.renameTemplate({ templateId: t.id, name, expectedName: t.name });
      if (!r.ok) {
        setFormError(r.message ?? 'Şablon adı değiştirilemedi');
        return;
      }
      setRenameFor(null);
      /* What the SERVER stored, so the toast reports the record rather than the keystrokes. */
      toast(`Şablonun adı «${r.message ?? name}» olarak kaydedildi`, 'ok');
    } finally {
      setBusy(false);
    }
  };

  const setArchived = async (id: string, archived: boolean): Promise<void> => {
    const r = await commands.setTemplateArchived({ templateId: id, archived });
    toast(
      r.ok
        ? archived
          ? 'Şablon arşivlendi'
          : 'Şablon arşivden çıkarıldı'
        : (r.message ?? 'Arşiv durumu değiştirilemedi'),
    );
  };

  const previewTpl = previewId === null ? undefined : db.templates.find((t) => t.id === previewId);
  const archiveTpl = archiveId === null ? undefined : db.templates.find((t) => t.id === archiveId);

  /* `#/admin/templates?new=1` is the Panel's own shortcut: the reference forwards, it never renders. */
  if (params.get('new')) return <Navigate to="/admin/templates/new" />;

  return (
    <section data-screen="templates">
      <div className="page-head">
        <div>
          <span className="eyebrow">Tasarım şablonları</span>
          <h1>Şablonlar</h1>
          <p className="lead">
            Her rozet, sertifika, diploma ve belge aynı generic şablondur: tasarımın üzerine dinamik alanlar
            yerleştirilir. Şablon sayısında ürün limiti yoktur.
          </p>
        </div>
        <div className="page-actions">
          {/* `data-b_new` and the toolbar's `data-built` are the reference's own bind-once markers.
              They carry no behaviour here; they are kept so the dumped DOM matches attribute for
              attribute. */}
          {mayEdit ? (
            <button
              className="btn btn-primary"
              type="button"
              id="newTemplateBtn"
              data-b_new="1"
              onClick={openNew}
            >
              + Yeni Şablon
            </button>
          ) : null}
        </div>
      </div>
      <div className="toolbar" id="tplFilters" data-built="1">
        <input
          className="input search"
          id="tplSearch"
          placeholder="Şablon ara (ad, kod, tür)…"
          autoComplete="off"
          aria-label="Şablon ara"
          value={filters.q}
          onChange={(e) => apply({ q: e.target.value })}
        />
        <select
          className="select"
          id="tplType"
          aria-label="Tür"
          value={filters.type}
          onChange={(e) => apply({ type: e.target.value })}
        >
          <option value="">Tüm türler</option>
          {CREDENTIAL_TYPES.map((x) => (
            <option value={x.key} key={x.key}>
              {x.tr}
            </option>
          ))}
        </select>
        <select
          className="select"
          id="tplHolder"
          aria-label="Alıcı türü"
          value={filters.holder}
          onChange={(e) => apply({ holder: e.target.value })}
        >
          <option value="">Kişi + şirket</option>
          {SUBJECT_TYPES.map((x) => (
            <option value={x.key} key={x.key}>
              {x.tr}
            </option>
          ))}
        </select>
        <select
          className="select"
          id="tplStatus"
          aria-label="Durum"
          value={filters.status}
          onChange={(e) => apply({ status: e.target.value })}
        >
          <option value="">Tüm durumlar</option>
          <option value="published">Yayında</option>
          <option value="draft">Taslak</option>
          <option value="archived">Arşiv</option>
        </select>
        <select
          className="select"
          id="tplSort"
          aria-label="Sıralama"
          value={filters.sort}
          onChange={(e) => apply({ sort: e.target.value })}
        >
          <option value="updated">Son güncelleme</option>
          <option value="name">Ada göre</option>
          <option value="count">Kayıt sayısına göre</option>
        </select>
        <span className="muted small" id="tplCountLabel">
          {`${list.length} / ${db.templates.length} şablon`}
        </span>
      </div>
      <div className="tpl-grid" id="templateGrid">
        {list.map((t) => {
          const lay = layoutOf(t);
          const release = currentRelease(t);
          const months = lay.validityPolicy?.months;
          return (
            <article
              className={`tpl-card ${t.status === 'archived' ? 'is-inactive' : ''}`}
              data-id={t.id}
              key={t.id}
            >
              {/* The engine returns the reference's own SVG string; React must not re-author it. */}
              <div className="thumb" dangerouslySetInnerHTML={{ __html: thumbs.get(t.id) ?? '' }} />
              <div className="body">
                <h2 className="card-title">{t.name}</h2>
                <div className="row">
                  <p className="small muted">{draftTitle(t, 'tr')}</p>
                  <span className={`pill ${statusPillClass(t.status)} none`}>
                    {`${TEMPLATE_STATUS_TR[t.status] ?? ''}${t.status === 'published' && release ? ` · v${release.releaseNumber}` : ''}`}
                  </span>
                </div>
                <div className="meta">
                  <span>{templateTypeLabel(t)}</span>
                  <span>{lay.subjectType === 'organization' ? 'Şirket' : 'Kişi'}</span>
                  <span>{`${(lay.layers ?? []).length} katman`}</span>
                  <span>{`${(lay.fieldDefinitions ?? []).length} alan`}</span>
                  <span>{`${fmtNum(tplCount(db, t.id))} kayıt`}</span>
                  <span>{`${lay.canvas.width}×${lay.canvas.height}`}</span>
                  <span>{months ? `${months} ay` : 'süresiz'}</span>
                  <span>{t.releases.length ? `${t.releases.length} release` : 'release yok'}</span>
                  <span title="Son güncelleme">
                    {fmtDate(String(t.updatedAt).slice(0, 10), 'tr', 'short')}
                  </span>
                  {t.seeded ? <span>demo</span> : null}
                </div>
              </div>
              <div className="foot">
                <Link className="btn btn-secondary btn-sm" to={`/admin/templates/${t.id}/editor`}>
                  Düzenle
                </Link>
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  data-preview={t.id}
                  onClick={() => {
                    setPreviewMode('short');
                    setPreviewId(t.id);
                  }}
                >
                  Önizle
                </button>
                {mayEdit ? (
                  <>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      data-dup={t.id}
                      onClick={() => openCopy(t)}
                    >
                      Kopyala
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      data-rename={t.id}
                      onClick={() => openRename(t)}
                    >
                      Adını değiştir
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      data-arch={t.id}
                      onClick={() => {
                        if (t.status === 'archived') void setArchived(t.id, false);
                        else setArchiveId(t.id);
                      }}
                    >
                      {t.status === 'archived' ? 'Arşivden çıkar' : 'Arşivle'}
                    </button>
                  </>
                ) : null}
              </div>
            </article>
          );
        })}
        {/* biome-ignore lint/a11y/useSemanticElements: the reference's own card is a div with role="button"; its markup is the acceptance target */}
        <div
          className="tpl-new"
          id="tplNewCard"
          role="button"
          tabIndex={0}
          onClick={openNew}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openNew();
            }
          }}
        >
          <div>
            <div className="plus">+</div>
            <b>Yeni şablon</b>
            <br />
            <span className="small">Sınırsız · PNG, JPG veya SVG tasarım · her ölçü ve tür</span>
          </div>
        </div>
      </div>

      {previewTpl ? (
        <Modal label="Şablon önizleme" wide onClose={() => setPreviewId(null)}>
          <ModalHead title={`${previewTpl.name} · önizleme`} onClose={() => setPreviewId(null)} />
          <div className="card-pad">
            <div
              style={{ background: 'var(--surface-3)', padding: '16px', borderRadius: '6px' }}
              id="tpPrev"
              dangerouslySetInnerHTML={{
                __html: templateThumbSVG(previewTpl, {
                  mode: previewMode,
                  publicBase: settings.publicBase,
                }),
              }}
            />
            <div className="chips" style={{ marginTop: '10px' }}>
              <button className="chip" type="button" data-pm="short" onClick={() => setPreviewMode('short')}>
                Kısa değer
              </button>
              <button className="chip" type="button" data-pm="long" onClick={() => setPreviewMode('long')}>
                Çok uzun değer
              </button>
            </div>
            <p className="small muted" style={{ marginTop: '8px' }}>
              {`Uzun değer, katmanın minimum puntosuna kadar otomatik küçültülür (shrink-to-fit). Canvas ${layoutOf(previewTpl).canvas.width}×${layoutOf(previewTpl).canvas.height} · ${layoutOf(previewTpl).canvas.orientation}.`}
            </p>
          </div>
        </Modal>
      ) : null}

      {/*
       * One dialog, two jobs — «Şablonu kopyala» and «Adını değiştir».
       *
       * They ask the same question about the same field, so they are one component: a name box, a
       * «Vazgeç» that creates and changes nothing, and one action button that is disabled while
       * its request is in flight, so a second press cannot make a second copy.
       */}
      {copyFor || renameFor ? (
        <Modal label={copyFor ? 'Şablonu kopyala' : 'Adını değiştir'} onClose={closeNameDialog}>
          <ModalHead
            title={copyFor ? 'Şablonu kopyala' : 'Şablonun adını değiştir'}
            onClose={closeNameDialog}
          />
          <div className="card-pad stack">
            <div className="field">
              <label htmlFor="tplNameInput">{copyFor ? 'Yeni şablon adı' : 'Şablon adı'}</label>
              <input
                className="input"
                id="tplNameInput"
                autoComplete="off"
                value={nameDraft}
                disabled={busy}
                onChange={(e) => setNameDraft(e.target.value)}
              />
              <span className="help">
                {
                  "Yalnız yönetim ekranlarında kullanılır: belge üzerindeki ad, doğrulama kodu, doğrulama bağlantısı ve yayımlanmış release'ler değişmez."
                }
              </span>
            </div>
            {formError === null ? null : (
              <div className="warn-box" role="alert" data-tpl-name-error>
                {formError}
              </div>
            )}
          </div>
          <div className="card-foot">
            <button
              className="btn btn-ghost"
              data-close="1"
              type="button"
              disabled={busy}
              onClick={closeNameDialog}
            >
              Vazgeç
            </button>
            <button
              className="btn btn-primary"
              id="tplNameGo"
              type="button"
              disabled={busy}
              onClick={() => {
                const t = copyFor ?? renameFor;
                if (!t) return;
                void (copyFor ? createCopy(t) : saveName(t));
              }}
            >
              {busy ? 'Gönderiliyor…' : copyFor ? 'Kopyayı oluştur' : 'Kaydet'}
            </button>
          </div>
        </Modal>
      ) : null}

      {archiveTpl ? (
        <Modal label="Arşivle" onClose={() => setArchiveId(null)}>
          <ModalHead title="Şablon arşivlensin mi?" onClose={() => setArchiveId(null)} />
          <div className="card-pad">
            <p>
              <b>{archiveTpl.name}</b>
              {` arşivlenirse toplu üretimde ve tek kayıt ekranında seçilemez; daha önce üretilmiş ${fmtNum(tplCount(db, archiveTpl.id))} kayıt kendi sürüm snapshot'larıyla yayında kalır.`}
            </p>
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" data-close="1" type="button" onClick={() => setArchiveId(null)}>
              Vazgeç
            </button>
            <button
              className="btn btn-dark"
              id="arcGo"
              type="button"
              onClick={() => {
                void setArchived(archiveTpl.id, true);
                setArchiveId(null);
              }}
            >
              Arşivle
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
